// The child half of the host's iframe bridge. Plain JS — no Vue, no store —
// so it lives in `@rdrive/helper-host-bridge`.
//
// HOST_APP_CONVENTIONS §3.2, implemented rule for rule:
//
//   * Message shape both ways is `{ action, payload }`. No request ids, no
//     version field, no `ready` handshake.
//   * The entire host API surface is the single action `getScopedToken`.
//   * "The host attaches its listener inside iframe.onload, after this page's
//     scripts have run. A request fired immediately at startup can be dropped.
//     Retry on an interval until the first reply."
//   * "Correlate by action only. Serialise requests; never have two
//     getScopedToken calls in flight."
//   * "If the host handler throws or resolves to a falsy value, nothing is
//     posted back. Time out."
//   * "Always pass a concrete target origin." Never '*'.
//
// When there is no host (standalone/dev) or no resolvable origin, `available`
// is false and every call resolves to null. The dashboard keeps running on the
// token it booted with.

const DEFAULT_TIMEOUT_MS = 10000;
const DEFAULT_RETRY_MS = 500;

/**
 * @typedef {Object} ScopedTokenReply
 * @property {string} token
 * @property {string} [scope]
 */

/**
 * @param {object} options
 * @param {string|null} options.hostOrigin  concrete origin; null disables the bridge
 * @param {Window} [options.target]         defaults to window.parent
 * @param {Window} [options.win]            listener host, defaults to window
 */
export function createHostBridge({ hostOrigin, target, win = window } = {}) {
  const parent = target || win.parent;

  // No host to talk to: standalone tab, or we could not establish the origin
  const available = Boolean(hostOrigin) && Boolean(parent) && parent !== win;

  // Serialisation: one in-flight request, shared by every caller
  let pending = null;

  /**
   * Ask the host for a fresh scoped token.
   *
   * @param {{ timeoutMs?: number, retryMs?: number }} [options]
   * @returns {Promise<ScopedTokenReply|null>} null when unavailable or timed out
   */
  const requestScopedToken = ({ timeoutMs = DEFAULT_TIMEOUT_MS, retryMs = DEFAULT_RETRY_MS } = {}) => {
    if (!available) return Promise.resolve(null);

    // Never two in flight — later callers join the request already running
    if (pending) return pending;

    pending = new Promise((resolve) => {
      let retryTimer = null;
      let timeoutTimer = null;

      const cleanup = () => {
        win.removeEventListener('message', onMessage);
        if (retryTimer) clearInterval(retryTimer);
        if (timeoutTimer) clearTimeout(timeoutTimer);
        retryTimer = null;
        timeoutTimer = null;
        pending = null;
      };

      function onMessage(event) {
        // Correlate by action only — but verify the sender first
        if (event.source !== parent) return;
        if (event.origin !== hostOrigin) return;
        const { action, payload } = event.data || {};
        if (action !== 'getScopedToken') return;

        cleanup();
        // The host posts nothing at all on a falsy handler result, so any
        // reply we do get should carry a payload; be defensive anyway.
        resolve(payload && payload.token ? payload : null);
      }

      win.addEventListener('message', onMessage);

      const post = () => {
        try {
          parent.postMessage({ action: 'getScopedToken' }, hostOrigin);
        } catch (error) {
          // A cross-origin post can throw if the frame went away
          console.warn('Host bridge post failed:', error.message);
        }
      };

      // The host may not have attached its listener yet, so keep asking
      post();
      retryTimer = setInterval(post, retryMs);

      timeoutTimer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeoutMs);
    });

    return pending;
  };

  return { available, requestScopedToken };
}
